<template>
  <div class="">
    <section class="about-item-area">
      <div class="container">
        <div class="bg-white" v-loading="isLoading">
          <div class="divider mt-5 mb-15"></div>
          <EventInfo @event-ready="onEventReady" />
          <el-divider></el-divider>

          <template v-if="event && event.closetime > Math.floor(Date.now() / 1000)">
            <!-- 已有投稿：展示投稿列表 -->
            <div v-if="manuscripts.length > 0" class="registered-wrapper">
              <!-- 顶部操作栏 -->
              <div class="order-actions-bar">
                <div class="order-summary">
                  <span class="order-count">{{ $t('manuscript.total', { count: manuscripts.length }) }}</span>
                </div>
                <el-button type="primary" size="medium" @click="goToForm">
                  <i class="el-icon-plus"></i> {{ $t('manuscript.submitAgain') || '再次投稿' }}
                </el-button>
              </div>

              <!-- 遍历所有投稿 -->
              <div 
                v-for="(item, index) in manuscripts" 
                :key="item.id || index"
                class="manuscript-card"
              >
                <!-- 顶部：标题 + 编辑按钮 -->
                <div class="manuscript-header">
                  <h4 class="manuscript-title">{{ item.title || '未命名投稿' }}</h4>
                  <el-button 
                    type="primary" 
                    size="small" 
                    plain
                    @click="goToEdit(item)"
                    class="edit-btn"
                  >
                    <i class="el-icon-edit"></i> {{ $t('common.edit') || '编辑' }}
                  </el-button>
                </div>

                <!-- 中间：基本信息 -->
                <div class="manuscript-body">
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">{{ $t('manuscript.labelAbstract') || '征稿主题' }}：</span>
                      <span class="info-value">{{ item.abstract || '-' }}</span>
                    </div>

                    <div class="info-item">
                      <span class="info-label">{{ $t('manuscript.labelAuthorFirst') || '作者' }}：</span>
                      <span class="info-value">{{ item.author?.realname || item.realname || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">{{ $t('manuscript.labelPhone') || '电话' }}：</span>
                      <span class="info-value">{{ item.author?.cellphone || item.cellphone || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">{{ $t('manuscript.labelPublishCompany') || '单位' }}：</span>
                      <span class="info-value">{{ item.author?.institution || item.company || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">{{ $t('manuscript.labelEmail') || '邮箱' }}：</span>
                      <span class="info-value">{{ item.author?.email || item.email || '-' }}</span>
                    </div>
                    <div class="info-item" v-if="item.createdat">
                      <span class="info-label">{{ $t('manuscript.submitTime') || '提交时间' }}：</span>
                      <span class="info-value">{{ formatTimestampToDateTime(item.createdat) }}</span>
                    </div>
                  </div>
                </div>

                <!-- 底部：附件 -->
                <div class="attachment-section" v-if="getAttachments(item.id).length > 0">
                  <div class="attachment-header">
                    <i class="el-icon-paperclip"></i>
                    <span class="attachment-title">{{ $t('manuscript.labelAttachments') }}</span>
                    <span class="attachment-count">（{{ $t('manuscript.fileToal', { count: getAttachments(item.id).length }) }}）</span>
                  </div>
                  <div class="attachment-list">
                    <div 
                      v-for="(file, idx) in getAttachments(item.id)" 
                      :key="idx"
                      class="attachment-item"
                      @click="handleDownloadFile(file)"
                    >
                      <div class="attachment-icon-wrapper" :style="{ backgroundColor: getFileColor(file.name) }">
                        <i :class="getFileIcon(file.name)"></i>
                      </div>
                      <div class="attachment-info-content">
                        <div class="attachment-name" :title="file.name || '海报文件'">
                          {{ file.name || '海报文件' }}
                        </div>
                        <div class="attachment-meta">
                          <span class="attachment-size">{{ formatFileSize(file.size || 0) }}</span>
                          <span class="attachment-type">{{ getFileExtension(file.name) }}</span>
                        </div>
                      </div>
                      <el-button 
                        type="text" 
                        size="mini" 
                        class="attachment-download-btn"
                        @click.stop="handleDownloadFile(file)"
                      >
                        {{ $t('manuscript.download') }}
                      </el-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="!isLoading">            
            <el-empty :description="$t('order.eventEnded') || '会议已结束~'"></el-empty>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import EventInfo from "@/components/Event/Info.vue";
import { getMeManuscripts, getMeManuscriptFile } from "@/api/user";
import { formatTimestampToDateTime } from '@/utils/dateUtil';
import { eventBus } from '@/utils/eventBus.js';
import config from '@/config';

export default {
  name: 'ManuscriptResult',
  components: {
    EventInfo
  },
  inject: ['dataWrapper', 'fetchStatus', 'eventFetchStatus'],
  data() {
    return {
      isLoading: true,
      eventid: config.EVENTID,
      event: null,
      manuscripts: [],
      attachmentsMap: {},
    };
  },
  computed: {
    isEn() {
      const locale = this.$i18n?.locale || localStorage.getItem('locale') || 'zh-cn';
      return locale === 'en' || locale === 'en-US';
    }
  },
  created() {
    const savedLocale = localStorage.getItem('locale');
    if (savedLocale) {
      this.currentLocale = savedLocale;
    } else if (this.$i18n?.locale) {
      this.currentLocale = this.$i18n.locale;
    }

    eventBus.$on('event-ready', this.onEventReady);
    eventBus.$on('language-changed', (lang) => {
      this.currentLocale = lang;
    });

    if (this.$i18n && this.$i18n.on) {
      this.$i18n.on('languageChanged', (lang) => {
        this.currentLocale = lang;
      });
    }

    this.loadManuscripts();
  },
  beforeDestroy() {
    if (this.$i18n && this.$i18n.off) {
      this.$i18n.off('languageChanged');
    }
    eventBus.$off('event-ready', this.onEventReady);
    eventBus.$off('language-changed');
  },
  methods: {
    formatTimestampToDateTime,

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '-';
      if (bytes < 1024) return bytes + 'B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
      return (bytes / 1024 / 1024).toFixed(1) + 'MB';
    },

    getFileExtension(fileName) {
      if (!fileName) return '';
      const ext = fileName.split('.').pop()?.toLowerCase() || '';
      return ext.toUpperCase();
    },

    getFileIcon(fileName) {
      if (!fileName) return 'el-icon-document';
      const ext = fileName.split('.').pop()?.toLowerCase() || '';
      const iconMap = {
        'pdf': 'el-icon-document',
        'ppt': 'el-icon-document',
        'pptx': 'el-icon-document',
        'jpg': 'el-icon-picture',
        'jpeg': 'el-icon-picture',
        'png': 'el-icon-picture',
        'gif': 'el-icon-picture',
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'xls': 'el-icon-document',
        'xlsx': 'el-icon-document',
        'zip': 'el-icon-document',
        'rar': 'el-icon-document',
        '7z': 'el-icon-document'
      };
      return iconMap[ext] || 'el-icon-document';
    },

    getFileColor(fileName) {
      if (!fileName) return '#409eff';
      const ext = fileName.split('.').pop()?.toLowerCase() || '';
      const colorMap = {
        'pdf': '#e53e3e',
        'ppt': '#f39c12',
        'pptx': '#f39c12',
        'jpg': '#27ae60',
        'jpeg': '#27ae60',
        'png': '#27ae60',
        'gif': '#27ae60',
        'doc': '#2980b9',
        'docx': '#2980b9',
        'xls': '#27ae60',
        'xlsx': '#27ae60',
        'zip': '#8e44ad',
        'rar': '#8e44ad',
        '7z': '#8e44ad'
      };
      return colorMap[ext] || '#409eff';
    },

    handleDownloadFile(file) {
      if (file.fileurl) {
        window.open(file.fileurl, '_blank');
      } else if (file.url) {
        window.open(file.url, '_blank');
      } else {
        this.$message.warning('文件地址不存在');
      }
    },

    getAttachments(manuscriptId) {
      return this.attachmentsMap[manuscriptId] || [];
    },

    async loadAttachments(manuscriptId) {
      try {
        const res = await getMeManuscriptFile(manuscriptId, { filter: { status: 100 } });
        if (res.code !== 200 || !Array.isArray(res.list)) {
          this.$set(this.attachmentsMap, manuscriptId, []);
          return;
        }

        const files = res.list.filter(item => item.meta?.type === 2);
        const fileList = files.map(item => ({
          id: item.id,
          name: item.name || '海报文件',
          fileurl: item.fileurl,
          size: item.size || 0,
          type: item.meta?.type
        }));

        this.$set(this.attachmentsMap, manuscriptId, fileList);
      } catch (error) {
        console.error('加载附件失败:', error);
        this.$set(this.attachmentsMap, manuscriptId, []);
      }
    },

    goToForm() {
      this.$router.push('/manuscript/form')
    },

    goToEdit(item) {
      this.$router.push({
        path: '/manuscript/form',
        query: { id: item.id }
      })
    },

    async loadManuscripts() {
      try {
        const res = await getMeManuscripts(0, 100);
        console.log('投稿列表:', res);
        if (res.code < 300 && res.list && res.list.length > 0) {
          this.manuscripts = res.list;
          for (const item of res.list) {
            await this.loadAttachments(item.id);
          }
        } else {
          this.manuscripts = [];
          this.goToForm();
        }
      } catch (error) {
        console.error('加载投稿列表失败:', error);
        this.manuscripts = [];
      }
    },

    onEventReady(eventData) {
      this.event = eventData;
      this.isLoading = false;
    },

    getStatusType(status) {
      const map = {
        'draft': 'info',
        'submitted': 'warning',
        'reviewing': 'warning',
        'accepted': 'success',
        'rejected': 'danger',
        'revised': 'warning',
        'withdrawn': 'info'
      };
      return map[status] || 'info';
    },

    getStatusText(status) {
      const map = {
        'draft': this.$t('manuscript.status.draft') || '草稿',
        'submitted': this.$t('manuscript.status.submitted') || '已提交',
        'reviewing': this.$t('manuscript.status.reviewing') || '审核中',
        'accepted': this.$t('manuscript.status.accepted') || '已录用',
        'rejected': this.$t('manuscript.status.rejected') || '已拒绝',
        'revised': this.$t('manuscript.status.revised') || '修改中',
        'withdrawn': this.$t('manuscript.status.withdrawn') || '已撤回'
      };
      return map[status] || status || '未知';
    }
  }
};
</script>

<style scoped>
.pc-padding {
  padding-left: 150px;
  padding-right: 150px;
}

@media (max-width: 768px) {
  .pc-padding {
    padding-left: 20px;
    padding-right: 20px;
  }
}

/* ===== 顶部操作栏 ===== */
.order-actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f0fe 100%);
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid #c2d8f8;
}

.order-summary {
  display: flex;
  align-items: center;
  gap: 20px;
}

.order-count {
  font-size: 16px;
  font-weight: 600;
  color: #1a3a6b;
}

/* ===== 投稿卡片 ===== */
.manuscript-card {
  background: #ffffff;
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.manuscript-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.manuscript-card:last-child {
  margin-bottom: 0;
}

/* ===== 卡片头部 ===== */
.manuscript-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f2f5;
  margin-bottom: 14px;
}

.manuscript-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a3a6b;
  margin: 0;
  flex: 1;
}

.edit-btn {
  flex-shrink: 0;
}

/* ===== 基本信息区域 ===== */
.manuscript-body {
  padding: 4px 0 6px 0;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px 32px;
}

.info-item {
  display: flex;
  align-items: baseline;
  font-size: 14px;
  color: #333;
  line-height: 2;
  padding: 2px 0;
  border-bottom: 1px dashed #f5f6f8;
}

.info-item:nth-last-child(-n+3) {
  border-bottom: none;
}

.info-label {
  color: #999;
  flex-shrink: 0;
  min-width: 52px;
  font-weight: 400;
}

.info-value {
  color: #1d2129;
  word-break: break-all;
  font-weight: 500;
}

/* ===== 附件区域（无背景框） ===== */
.attachment-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f0f2f5;
}

.attachment-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.attachment-header i {
  color: #409eff;
  font-size: 16px;
}

.attachment-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a3a6b;
}

.attachment-count {
  color: #999;
  font-weight: 400;
  font-size: 13px;
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e8ecf1;
  cursor: pointer;
  transition: all 0.25s ease;
}

.attachment-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.12);
  transform: translateY(-1px);
}

.attachment-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.attachment-icon-wrapper i {
  font-size: 18px;
  color: #ffffff;
}

.attachment-info-content {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-size: 14px;
  font-weight: 500;
  color: #1d2129;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 1px;
}

.attachment-size {
  font-size: 12px;
  color: #999;
}

.attachment-type {
  font-size: 11px;
  color: #666;
  background: #f0f2f5;
  padding: 0 8px;
  border-radius: 3px;
  line-height: 18px;
  font-weight: 500;
}

.attachment-download-btn {
  color: #409eff;
  font-size: 13px;
  padding: 4px 8px;
  flex-shrink: 0;
}

.attachment-download-btn:hover {
  color: #66b1ff;
}

.attachment-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
  color: #bbb;
}

.attachment-empty i {
  font-size: 28px;
  margin-bottom: 6px;
}

.attachment-empty span {
  font-size: 13px;
}

/* ===== 无数据 ===== */
.no-data-wrapper {
  padding: 40px 0;
}

/* ===== 响应式 ===== */
@media (max-width: 992px) {
  .info-grid {
    grid-template-columns: 1fr 1fr;
    gap: 6px 24px;
  }
  
  .info-item:nth-last-child(-n+2) {
    border-bottom: none;
  }
}

@media (max-width: 768px) {
  .order-actions-bar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .order-summary {
    justify-content: center;
  }
  
  .order-actions-bar .el-button {
    width: 100%;
    justify-content: center;
  }

  .manuscript-card {
    padding: 16px;
  }

  .manuscript-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .info-grid {
    grid-template-columns: 1fr 1fr;
    gap: 4px 16px;
  }

  .info-item {
    font-size: 13px;
    line-height: 1.8;
  }
  
  .info-item:nth-last-child(-n+2) {
    border-bottom: none;
  }

  .info-label {
    min-width: 44px;
  }

  .attachment-item {
    padding: 6px 10px;
  }

  .attachment-icon-wrapper {
    width: 30px;
    height: 30px;
  }

  .attachment-icon-wrapper i {
    font-size: 14px;
  }

  .attachment-name {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .info-grid {
    grid-template-columns: 1fr;
    gap: 2px;
  }
  
  .info-item {
    border-bottom: 1px dashed #f5f6f8;
  }
  
  .info-item:last-child {
    border-bottom: none;
  }

  .attachment-item {
    flex-wrap: wrap;
  }

  .attachment-download-btn {
    width: 100%;
    text-align: center;
    margin-top: 2px;
  }
}
</style>